import { BusinessGraphComparableFactV1Schema, BusinessGraphContradictionResultV1Schema, COMPARABLE_FACT_STATES, FIELD_PATH_BUSINESS_NAME, FIELD_PATH_HOURS, FIELD_PATH_PRIMARY_EMAIL, FIELD_PATH_PRIMARY_PHONE, FIELD_PATH_SERVICE_AREA_NAME, FIELD_PATH_SERVICE_NAME, } from "../contracts/business-graph-contradiction.js";
import { AgencyIdSchema, ProjectIdSchema } from "../primitives/ids.js";
import { extractComparableFactsFromBusinessGraph } from "./extract-comparable-facts-from-business-graph.js";
import { normalizeComparableFactValue } from "./normalize-comparable-fact-value.js";
/**
 * Deterministic code-unit ordering — independent of host locale.
 * Preferred over localeCompare for cross-platform identity stability.
 */
export function compareStableText(left, right) {
    return left < right ? -1 : left > right ? 1 : 0;
}
/**
 * Typed fail-closed input error for contradiction detection.
 * Callers (readiness / worker) must treat this as INVALID_INPUT — never CLEAR.
 */
export class BusinessGraphContradictionInputError extends Error {
    code;
    constructor(code, message) {
        super(message);
        this.name = "BusinessGraphContradictionInputError";
        this.code = code;
    }
}
const KNOWN_FIELD_PATHS = new Set([
    FIELD_PATH_PRIMARY_PHONE,
    FIELD_PATH_PRIMARY_EMAIL,
    FIELD_PATH_BUSINESS_NAME,
    FIELD_PATH_HOURS,
    FIELD_PATH_SERVICE_NAME,
    FIELD_PATH_SERVICE_AREA_NAME,
]);
function codeForFieldPath(fieldPath) {
    switch (fieldPath) {
        case FIELD_PATH_PRIMARY_PHONE:
            return "CONTACT_PHONE_CONFLICT";
        case FIELD_PATH_PRIMARY_EMAIL:
            return "CONTACT_EMAIL_CONFLICT";
        case FIELD_PATH_BUSINESS_NAME:
            return "BUSINESS_NAME_CONFLICT";
        case FIELD_PATH_HOURS:
            return "HOURS_DAY_CONFLICT";
        case FIELD_PATH_SERVICE_NAME:
            return "SERVICE_ID_NAME_CONFLICT";
        case FIELD_PATH_SERVICE_AREA_NAME:
            return "SERVICE_AREA_ID_NAME_CONFLICT";
        default:
            throw new BusinessGraphContradictionInputError("UNKNOWN_FIELD_PATH", `Unsupported comparable fieldPath for contradiction detection: ${fieldPath}`);
    }
}
function impactForCode(code) {
    switch (code) {
        case "CONTACT_PHONE_CONFLICT":
            return {
                severity: "BLOCK",
                blocking: true,
                blocksGeneration: true,
                blocksApproval: true,
                affectedOutputs: ["website_contact", "generation", "activation"],
                message: "Conflicting primary phone values for the same business identity.",
                suggestedResolution: "Verify the correct primary phone and reject or edit conflicting candidates.",
            };
        case "CONTACT_EMAIL_CONFLICT":
            return {
                severity: "BLOCK",
                blocking: true,
                blocksGeneration: true,
                blocksApproval: true,
                affectedOutputs: ["website_contact", "generation", "activation"],
                message: "Conflicting primary email values for the same business identity.",
                suggestedResolution: "Verify the correct primary email and reject or edit conflicting candidates.",
            };
        case "BUSINESS_NAME_CONFLICT":
            return {
                severity: "BLOCK",
                blocking: true,
                blocksGeneration: true,
                blocksApproval: true,
                affectedOutputs: ["website_identity", "generation", "activation"],
                message: "Conflicting business name values for the same identity field.",
                suggestedResolution: "Confirm the canonical business name and reject superseded candidates.",
            };
        case "HOURS_DAY_CONFLICT":
            return {
                severity: "BLOCK",
                blocking: true,
                blocksGeneration: true,
                blocksApproval: true,
                affectedOutputs: ["website_hours", "generation", "activation"],
                message: "Conflicting business hours for the same day of week.",
                suggestedResolution: "Keep one open/close pair per day or scope hours to different locations.",
            };
        case "SERVICE_ID_NAME_CONFLICT":
            return {
                severity: "BLOCK",
                blocking: true,
                blocksGeneration: true,
                blocksApproval: true,
                affectedOutputs: ["website_services", "generation", "activation"],
                message: "Same service id has conflicting names.",
                suggestedResolution: "Align the service name or use distinct service ids.",
            };
        case "SERVICE_AREA_ID_NAME_CONFLICT":
            return {
                severity: "BLOCK",
                blocking: true,
                blocksGeneration: true,
                blocksApproval: true,
                affectedOutputs: ["website_service_areas", "generation", "activation"],
                message: "Same service-area id has conflicting names.",
                suggestedResolution: "Align the area name or use distinct area ids.",
            };
    }
}
function groupKey(fieldPath, scopeKey) {
    return `${fieldPath}\u0000${scopeKey}`;
}
function parseFactsOrThrow(facts) {
    try {
        return facts.map((f) => BusinessGraphComparableFactV1Schema.parse(f));
    }
    catch {
        throw new BusinessGraphContradictionInputError("MISSING_TENANT_OR_PROJECT", "Contradiction detection rejected: fact is missing required agencyId/projectId or is otherwise malformed");
    }
}
/**
 * Deterministic, field-aware contradiction detection.
 * - Never mutates input facts or BusinessGraph
 * - Never compares across tenants (mixed/foreign/missing tenant → INVALID_INPUT throw)
 * - Excludes REJECTED / SUPERSEDED facts
 * - Does not call AI
 * - Does not read wall clock / Date.now / random UUID
 */
export function detectBusinessGraphContradictions(input) {
    const agencyId = AgencyIdSchema.parse(input.agencyId);
    const projectId = ProjectIdSchema.parse(input.projectId);
    const checkedAt = input.checkedAt;
    if (typeof checkedAt !== "string" || checkedAt.length < 1) {
        throw new BusinessGraphContradictionInputError("MISSING_TENANT_OR_PROJECT", "Contradiction detection requires an explicit checkedAt observation timestamp from the caller");
    }
    const parsedFacts = parseFactsOrThrow(input.facts);
    // Tenant fail-closed: any foreign / mismatched tenant fact rejects the evaluation.
    // Never silently drop to CLEAR.
    const foreign = parsedFacts.filter((f) => f.agencyId !== agencyId || f.projectId !== projectId);
    if (foreign.length > 0) {
        const agencyMismatch = foreign.some((f) => f.agencyId !== agencyId);
        const projectMismatch = foreign.some((f) => f.projectId !== projectId);
        let code;
        if (foreign.length === parsedFacts.length) {
            code = "FOREIGN_TENANT_FACTS";
        }
        else if (agencyMismatch && !projectMismatch) {
            code = "MIXED_TENANT_FACTS";
        }
        else if (projectMismatch && !agencyMismatch) {
            code = "MIXED_PROJECT_FACTS";
        }
        else {
            code = "MIXED_TENANT_FACTS";
        }
        throw new BusinessGraphContradictionInputError(code, `Contradiction detection rejected: ${foreign.length} fact(s) do not match evaluation tenant ${agencyId}/${projectId}`);
    }
    const eligible = parsedFacts.filter((f) => COMPARABLE_FACT_STATES.includes(f.state));
    const groups = new Map();
    for (const fact of eligible) {
        if (!KNOWN_FIELD_PATHS.has(fact.fieldPath)) {
            throw new BusinessGraphContradictionInputError("UNKNOWN_FIELD_PATH", `Unsupported comparable fieldPath for contradiction detection: ${fact.fieldPath}`);
        }
        if (!fact.value.trim())
            continue;
        const key = groupKey(fact.fieldPath, fact.scopeKey);
        const list = groups.get(key);
        if (list)
            list.push(fact);
        else
            groups.set(key, [fact]);
    }
    const contradictions = [];
    for (const [, groupFacts] of groups) {
        const byNormalized = new Map();
        for (const fact of groupFacts) {
            const normalized = normalizeComparableFactValue(fact.fieldPath, fact.value);
            if (!normalized)
                continue;
            const bucket = byNormalized.get(normalized);
            if (bucket)
                bucket.push(fact);
            else
                byNormalized.set(normalized, [fact]);
        }
        if (byNormalized.size < 2)
            continue;
        const representatives = [];
        const normalizedValues = [];
        for (const [normalized, bucket] of [...byNormalized.entries()].sort(([a], [b]) => compareStableText(a, b))) {
            normalizedValues.push(normalized);
            const sortedBucket = [...bucket].sort((a, b) => compareStableText(a.factId, b.factId));
            representatives.push(sortedBucket[0]);
        }
        const fieldPath = representatives[0].fieldPath;
        const scopeKey = representatives[0].scopeKey;
        const code = codeForFieldPath(fieldPath);
        const impact = impactForCode(code);
        const factIds = representatives.map((f) => f.factId).sort(compareStableText);
        const displayValues = representatives.map((f) => f.value);
        const sourceRefs = [
            ...new Set(representatives
                .map((f) => f.sourceRef)
                .filter((s) => typeof s === "string" && s.length > 0)),
        ].sort(compareStableText);
        const revisionIds = [
            ...new Set(representatives
                .map((f) => f.revisionId)
                .filter((s) => s != null)
                .map((s) => String(s))),
        ].sort(compareStableText);
        const candidateIds = [
            ...new Set(representatives
                .map((f) => f.candidateId)
                .filter((s) => typeof s === "string" && s.length > 0)),
        ].sort(compareStableText);
        const sortKey = `${code}|${fieldPath}|${scopeKey}|${normalizedValues.join(",")}`;
        const contradictionId = `cx:${sortKey}`;
        contradictions.push({
            contradictionId,
            code,
            severity: impact.severity,
            fieldPath,
            scopeKey,
            normalizedValues,
            displayValues,
            sourceRefs,
            revisionIds,
            candidateIds,
            factIds,
            agencyId,
            projectId,
            blocking: impact.blocking,
            readinessImpact: {
                blocksGeneration: impact.blocksGeneration,
                blocksApproval: impact.blocksApproval,
                affectedOutputs: impact.affectedOutputs,
            },
            message: impact.message,
            suggestedResolution: impact.suggestedResolution,
            sortKey,
        });
    }
    contradictions.sort((a, b) => compareStableText(a.sortKey, b.sortKey));
    const hasBlock = contradictions.some((c) => c.severity === "BLOCK");
    const hasWarn = contradictions.some((c) => c.severity === "WARN");
    const status = hasBlock ? "BLOCKED" : hasWarn ? "WARNINGS" : "CLEAR";
    const affectedOutputs = [
        ...new Set(contradictions.flatMap((c) => c.readinessImpact.affectedOutputs)),
    ].sort(compareStableText);
    return BusinessGraphContradictionResultV1Schema.parse({
        schemaVersion: "businessgraph.contradictionResult.v1",
        agencyId,
        projectId,
        revisionId: input.revisionId,
        evaluatedFromBusinessGraphSnapshotHash: input.evaluatedFromBusinessGraphSnapshotHash,
        checkedAt,
        status,
        contradictions,
        readinessImpact: {
            blocksGeneration: contradictions.some((c) => c.readinessImpact.blocksGeneration),
            blocksApproval: contradictions.some((c) => c.readinessImpact.blocksApproval),
            affectedOutputs,
        },
        detectionCompleted: true,
    });
}
/**
 * Detect contradictions within one graph (+ optional trusted active-head facts).
 * Does not mutate graph. Propagates BusinessGraphContradictionInputError fail-closed.
 */
export function detectBusinessGraphContradictionsFromGraph(input) {
    const state = input.state ?? "CANDIDATE";
    const extracted = extractComparableFactsFromBusinessGraph(input.graph, {
        state,
        sourceRef: input.sourceRef ?? `revision:${input.graph.revisionId}`,
    });
    return detectBusinessGraphContradictions({
        agencyId: input.graph.agencyId,
        projectId: input.graph.projectId,
        facts: [...extracted, ...(input.additionalFacts ?? [])],
        checkedAt: input.checkedAt,
        revisionId: input.graph.revisionId,
        evaluatedFromBusinessGraphSnapshotHash: input.graph.snapshotHash,
    });
}
/** True when blocking contradiction or INVALID_INPUT must stop generation/activation. */
export function hasBlockingBusinessGraphContradiction(result) {
    if (result.status === "INVALID_INPUT")
        return true;
    return result.status === "BLOCKED" && result.readinessImpact.blocksGeneration;
}
/**
 * Synthetic INVALID_INPUT result for typed input failures
 * (mixed/foreign tenant, unknown field path) so readiness always attaches a result.
 * Does not embed foreign fact values.
 */
export function invalidInputContradictionResult(input, error) {
    void error; // code retained on hardBlockers / audit — never embed foreign fact values here
    const agencyId = AgencyIdSchema.parse(input.agencyId);
    const projectId = ProjectIdSchema.parse(input.projectId);
    return BusinessGraphContradictionResultV1Schema.parse({
        schemaVersion: "businessgraph.contradictionResult.v1",
        agencyId,
        projectId,
        revisionId: input.revisionId,
        evaluatedFromBusinessGraphSnapshotHash: input.evaluatedFromBusinessGraphSnapshotHash,
        checkedAt: input.checkedAt,
        status: "INVALID_INPUT",
        contradictions: [],
        readinessImpact: {
            blocksGeneration: true,
            blocksApproval: true,
            affectedOutputs: ["generation", "activation"].sort(compareStableText),
        },
        detectionCompleted: true,
    });
}
/** @deprecated Use invalidInputContradictionResult — kept as alias for call-site clarity. */
export function blockedContradictionResultForInputError(input, error) {
    return invalidInputContradictionResult(input, error);
}
//# sourceMappingURL=detect-business-graph-contradictions.js.map